"use client";

import { useUser } from "@/lib/auth/client";
import { useRouter, usePathname } from "next/navigation";
import { useEffect, useLayoutEffect, useState, ReactNode } from "react";
import { NoxusLoadingScreen } from "./UI/NoxusLoadingScreen";
import { getCached, setCache, CACHE_KEYS } from "@/utils/queryCache";

const AuthState = {
  INIT: "init",
  CACHED: "cached",
  UNCACHED: "uncached",
} as const;

type AuthStateValue = (typeof AuthState)[keyof typeof AuthState];

export default function Authenticated({ children }: { children: ReactNode }) {
  const { user, isLoading } = useUser();
  const router = useRouter();
  const pathname = usePathname();

  // INIT on both server and client hydration (no mismatch).
  // useLayoutEffect resolves to CACHED or UNCACHED before the browser paints.
  const [authState, setAuthState] = useState<AuthStateValue>(AuthState.INIT);
  useLayoutEffect(() => {
    const cached = getCached<boolean>(CACHE_KEYS.AUTH_USER) === true;
    setAuthState(cached ? AuthState.CACHED : AuthState.UNCACHED);
  }, []);

  useEffect(() => {
    if (user) {
      setCache(CACHE_KEYS.AUTH_USER, true);
    }
  }, [user]);

  useEffect(() => {
    if (!isLoading && !user) {
      // The in-app /changelog has a public sibling at /public/changelog —
      // bounce unauthenticated visitors there instead of the login screen.
      if (pathname === "/changelog") {
        router.replace("/public/changelog");
        return;
      }

      const here =
        typeof window !== "undefined"
          ? `${window.location.pathname}${window.location.search}${window.location.hash}`
          : pathname || "/";
      router.push(`/welcome?returnTo=${encodeURIComponent(here || "/")}`);
    }
  }, [user, isLoading, router, pathname]);

  // Before useLayoutEffect resolves — never painted (SSR + hydration placeholder)
  if (authState === AuthState.INIT) {
    return null;
  }

  // Returning users: skip loading screen, render children optimistically
  if (isLoading && authState === AuthState.CACHED) {
    return <>{children}</>;
  }

  // New/unauthenticated users: show loading screen until Auth0 resolves
  if (isLoading || !user) {
    return <NoxusLoadingScreen />;
  }

  return <>{children}</>;
}
